import { receivedRequestProvider } from '@core/domain/product'

const normalizeRequestIds = requestIds => Array.from(new Set(
    (Array.isArray(requestIds) ? requestIds : [])
        .map(requestId => Number(requestId))
        .filter(requestId => Number.isInteger(requestId) && requestId > 0),
))

export default {
    props: {
        mode: {
            type: String,
            default: 'single',
            validator: value => ['single', 'bulk'].includes(value),
        },
        additionalRequestIds: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            bulkReplyCandidates: [],
            bulkReplyCandidatesError: null,
            bulkReplyCandidatesLoaded: false,
            bulkReplyCandidatesLoading: false,
            bulkReplyCandidatesPromise: null,
            selectedBulkReplyRequestIds: this.mode === 'bulk'
                ? normalizeRequestIds(this.additionalRequestIds)
                : [],
            bulkReplySelectionUpdating: false,
        }
    },
    computed: {
        isExternalBulkReplyMode() {
            return this.mode === 'bulk'
        },
        isExternalBulkReplySelectionValid() {
            return this.isExternalBulkReplyMode
                && this.bulkReplyCandidatesLoaded
                && !this.bulkReplyCandidatesLoading
                && !this.bulkReplyCandidatesError
                && this.selectedBulkReplyRequestIds.length > 0
        },
        supportsLateBulkReplySelection() {
            return true
        },
        hasBulkReplyCandidates() {
            return this.bulkReplyCandidates.length > 0
        },
        isBulkReplyCandidatesStepVisible() {
            return !this.isExternalBulkReplyMode
                && this.supportsLateBulkReplySelection
                && (this.hasBulkReplyCandidates || this.bulkReplyCandidatesError !== null)
        },
    },
    created() {
        if (this.isExternalBulkReplyMode || this.supportsLateBulkReplySelection) {
            this.loadBulkReplyCandidates()
        }
    },
    methods: {
        loadBulkReplyCandidates({ force = false } = {}) {
            if (this.bulkReplyCandidatesLoading) {
                return this.bulkReplyCandidatesPromise
            }

            if (this.bulkReplyCandidatesLoaded && !force) {
                return Promise.resolve(this.bulkReplyCandidates)
            }

            this.bulkReplyCandidatesLoading = true
            this.bulkReplyCandidatesError = null
            this.bulkReplyCandidatesPromise = receivedRequestProvider(this.id)
                .getBulkReplyCandidates()
                .then(({ data }) => {
                    this.bulkReplyCandidates = Array.isArray(data.candidates) ? data.candidates : []
                    this.bulkReplyCandidatesLoaded = true
                    this.selectedBulkReplyRequestIds = this.selectedBulkReplyRequestIds
                        .filter(id => this.bulkReplyCandidates.some(
                            candidate => Number(candidate.requestId) === Number(id),
                        ))

                    if (this.isExternalBulkReplyMode) {
                        return this.syncBulkReplyRouteQuery(this.selectedBulkReplyRequestIds)
                            .then(() => this.bulkReplyCandidates)
                    }

                    return this.bulkReplyCandidates
                })
                .catch(error => {
                    this.bulkReplyCandidates = []
                    this.bulkReplyCandidatesLoaded = false
                    this.bulkReplyCandidatesError = error
                    return []
                })
                .finally(() => {
                    this.bulkReplyCandidatesLoading = false
                })

            return this.bulkReplyCandidatesPromise
        },
        updateSelectedBulkReplyRequestIds(requestIds) {
            this.selectedBulkReplyRequestIds = normalizeRequestIds(requestIds)
        },
        async removeBulkReplyRequest(requestId) {
            const previousRequestIds = [...this.selectedBulkReplyRequestIds]
            this.bulkReplySelectionUpdating = true
            this.selectedBulkReplyRequestIds = this.selectedBulkReplyRequestIds
                .filter(id => Number(id) !== Number(requestId))

            try {
                await this.syncBulkReplyRouteQuery(this.selectedBulkReplyRequestIds)
                this.bulkReplyCandidatesError = null
            } catch (error) {
                this.selectedBulkReplyRequestIds = previousRequestIds
                this.bulkReplyCandidatesError = error
            } finally {
                this.bulkReplySelectionUpdating = false
            }
        },
        syncBulkReplyRouteQuery(requestIds) {
            if (!this.isExternalBulkReplyMode || !this.$router || !this.$route) {
                return Promise.resolve()
            }

            const query = { ...this.$route.query }
            if (requestIds.length > 0) {
                query.additionalRequestIds = requestIds.join(',')
            } else {
                delete query.additionalRequestIds
            }

            return this.$router.replace({ query }).catch(error => {
                if (error && error.name === 'NavigationDuplicated') {
                    return
                }
                throw error
            })
        },
    },
}
